<template>
  <div class="campaign-management">
    <!-- Header + Filters -->
    <b-row class="mb-2">
      <b-col cols="12">
        <b-card>
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h2 class="mb-0 font-weight-bold text-dark">
                <feather-icon icon="PhoneCallIcon" size="28" class="mr-1 text-primary" />
                Campaign Management
              </h2>
              <p class="text-muted mb-0 mt-1">Manage and track your marketing campaigns</p>
            </div>
            <b-button
              variant="primary"
              size="lg"
              class="mt-2 mt-md-0"
              :disabled="isStartingCampaign"
              @click="startCampaign"
            >
              <b-spinner v-if="isStartingCampaign" small class="mr-1" />
              <feather-icon v-else icon="PlayIcon" size="18" class="mr-1" />
              {{ isStartingCampaign ? 'Starting...' : 'Start Campaign' }}
            </b-button>
          </div>
          <hr class="my-2" />
          <b-row class="align-items-end">
            <b-col md="3" sm="6" class="mb-2 mb-md-0">
              <b-form-group label="From Date" class="mb-0">
                <b-form-input type="date" v-model="filters.fromDate" @input="onDateFilterChange" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6" class="mb-2 mb-md-0">
              <b-form-group label="To Date" class="mb-0">
                <b-form-input type="date" v-model="filters.toDate" @input="onDateFilterChange" :min="filters.fromDate || null" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6" class="mb-2 mb-md-0">
              <b-form-group label="Status" class="mb-0">
                <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  @change="onFilterChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6" class="text-md-right">
              <b-button
                variant="outline-secondary"
                @click="resetFilters"
              >
                <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
                Reset Filters
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Campaign Statistics -->
    <b-row class="mb-2">
      <b-col lg="3" md="6">
        <b-card class="stat-card bg-light-primary">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-primary">
              <feather-icon icon="PhoneIcon" size="24" class="text-white" />
            </div>
            <div class="ml-2">
              <h4 class="mb-0 font-weight-bold">{{ stats.totalCalls || 0 }}</h4>
              <p class="mb-0 text-muted">Total Calls</p>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card bg-light-success">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-success">
              <feather-icon icon="ThumbsUpIcon" size="24" class="text-white" />
            </div>
            <div class="ml-2">
              <h4 class="mb-0 font-weight-bold">{{ stats.interested || 0 }}</h4>
              <p class="mb-0 text-muted">Interested</p>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card bg-light-warning">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-warning">
              <feather-icon icon="ThumbsDownIcon" size="24" class="text-white" />
            </div>
            <div class="ml-2">
              <h4 class="mb-0 font-weight-bold">{{ stats.notInterested || 0 }}</h4>
              <p class="mb-0 text-muted">Not Interested</p>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3" md="6">
        <b-card class="stat-card bg-light-info">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-info">
              <feather-icon icon="PercentIcon" size="24" class="text-white" />
            </div>
            <div class="ml-2">
              <h4 class="mb-0 font-weight-bold">{{ conversionRate }}%</h4>
              <p class="mb-0 text-muted">Conversion Rate</p>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Campaign Results Table -->
    <b-row>
      <b-col cols="12">
        <b-card>
          <b-card-header class="d-flex justify-content-between align-items-center px-0 pt-0">
            <h4 class="mb-0 font-weight-bold text-dark">Campaign Results</h4>
            <div class="d-flex align-items-center">
              <b-input-group class="mr-2" style="width: 250px;">
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Search customers..."
                  @keyup.enter="onSearch"
                />
                <b-input-group-append>
                  <b-button variant="outline-primary" @click="onSearch">
                    <feather-icon icon="SearchIcon" size="16" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-button
                variant="outline-primary"
                @click="exportData"
                :disabled="isExporting"
              >
                <b-spinner v-if="isExporting" small class="mr-1" />
                <feather-icon v-else icon="DownloadIcon" size="16" class="mr-1" />
                Export
              </b-button>
            </div>
          </b-card-header>

          <b-table
            :items="campaignResults"
            :fields="tableFields"
            :busy="isLoading"
            responsive
            hover
            striped
            show-empty
            empty-text="No campaign data available"
          >
            <!-- Status Column -->
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.value)">
                {{ formatStatus(data.value) }}
              </b-badge>
            </template>

            <!-- Interested Column -->
            <template #cell(interested)="data">
              <b-badge :variant="data.value ? 'success' : 'secondary'">
                <feather-icon :icon="data.value ? 'CheckIcon' : 'XIcon'" size="12" class="mr-1" />
                {{ data.value ? 'Yes' : 'No' }}
              </b-badge>
            </template>

            <!-- Call Duration Column -->
            <template #cell(call_duration)="data">
              <span class="text-muted">
                <feather-icon icon="ClockIcon" size="14" class="mr-1" />
                {{ formatDuration(data.value) }}
              </span>
            </template>

            <!-- Call Date Column -->
            <template #cell(call_date)="data">
              <span class="text-muted">
                <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                {{ formatDate(data.value) }}
              </span>
            </template>

            <!-- Actions Column -->
            <template #cell(actions)="data">
              <div class="d-flex gap-1">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  v-b-tooltip.hover
                  title="View Details"
                  @click="viewDetails(data.item)"
                >
                  <feather-icon icon="EyeIcon" size="14" />
                </b-button>
              </div>
            </template>
          </b-table>

          <!-- Pagination -->
          <b-row v-if="campaignResults.length > 0" class="mt-2 align-items-center">
            <b-col md="6" class="mb-2 mb-md-0">
              <span class="text-muted">Showing {{ paginationInfo.from }} to {{ paginationInfo.to }} of {{ paginationInfo.total }} entries</span>
            </b-col>
            <b-col md="6" class="d-flex justify-content-md-end">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                @change="onPageChange"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- View Details Modal -->
    <b-modal
      v-model="showDetailsModal"
      title="Campaign Details"
      centered
      size="lg"
      hide-footer
    >
      <div v-if="selectedItem">
        <b-row>
          <b-col md="6" class="mb-2">
            <strong>Customer Name:</strong>
            <p class="mb-0">{{ selectedItem.customer_name }}</p>
          </b-col>
          <b-col md="6" class="mb-2">
            <strong>Phone Number:</strong>
            <p class="mb-0">{{ selectedItem.phone_number }}</p>
          </b-col>
          <b-col md="6" class="mb-2">
            <strong>Status:</strong>
            <p class="mb-0">
              <b-badge :variant="getStatusVariant(selectedItem.status)">
                {{ formatStatus(selectedItem.status) }}
              </b-badge>
            </p>
          </b-col>
          <b-col md="6" class="mb-2">
            <strong>Interested:</strong>
            <p class="mb-0">
              <b-badge :variant="selectedItem.interested ? 'success' : 'secondary'">
                {{ selectedItem.interested ? 'Yes' : 'No' }}
              </b-badge>
            </p>
          </b-col>
          <b-col md="6" class="mb-2">
            <strong>Call Duration:</strong>
            <p class="mb-0">{{ formatDuration(selectedItem.call_duration) }}</p>
          </b-col>
          <b-col md="6" class="mb-2">
            <strong>Call Date:</strong>
            <p class="mb-0">{{ formatDate(selectedItem.call_date) }}</p>
          </b-col>
          <b-col cols="12" class="mb-2" v-if="selectedItem.notes">
            <strong>Notes:</strong>
            <ul class="mb-0 mt-1 pl-3">
              <li v-for="(note, index) in formatNotes(selectedItem.notes)" :key="index">
                {{ note }}
              </li>
            </ul>
          </b-col>
          <b-col cols="12" v-else>
            <strong>Notes:</strong>
            <p class="mb-0 text-muted">No notes available</p>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- Add Notes Modal -->
    <b-modal
      id="notes-modal"
      title="Add Notes"
      centered
      hide-header-close
      @ok="saveNotes"
      @cancel="cancelNotes"
    >
      <b-form-group label="Notes">
        <b-form-textarea
          v-model="currentNotes"
          rows="4"
          placeholder="Enter your notes about this call..."
        />
      </b-form-group>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok" :disabled="!currentNotes.trim()">
          Save Notes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";



export default {
  name: "CampaignView",
  data() {
    return {
      isStartingCampaign: false,
      isLoading: false,
      isExporting: false,
      searchQuery: "",
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filters: {
        fromDate: null,
        toDate: null,
        status: null,
      },
      statusOptions: [
        { value: null, text: "All Status" },
        { value: "completed", text: "Completed" },
        { value: "pending", text: "Pending" },
        { value: "failed", text: "Failed" },
        { value: "in_progress", text: "In Progress" },
      ],
      stats: {
        totalCalls: 0,
        interested: 0,
        notInterested: 0,
      },
      campaignResults: [],
      currentNotes: "",
      currentItem: null,
      showDetailsModal: false,
      selectedItem: null,
      tableFields: [
        { key: "id", label: "ID", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "customer_name", label: "Customer Name", sortable: true },
        { key: "phone_number", label: "Phone Number", sortable: true },
        { key: "status", label: "Call Status", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "interested", label: "Interested", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "call_duration", label: "Duration", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "call_date", label: "Call Date", sortable: true },
        { key: "actions", label: "Actions", sortable: false, thClass: "text-center", tdClass: "text-center" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getLoggedInUser: "appData/getLoggedInUser",
    }),
    conversionRate() {
      if (this.stats.totalCalls === 0) return 0;
      return Math.round((this.stats.interested / this.stats.totalCalls) * 100);
    },
    paginationInfo() {
      const from = (this.currentPage - 1) * this.perPage + 1;
      const to = Math.min(this.currentPage * this.perPage, this.totalRows);
      return {
        from: from > this.totalRows ? 0 : from,
        to: to > this.totalRows ? this.totalRows : to,
        total: this.totalRows,
      };
    },
  },
  mounted() {
    this.loadCampaignData();
  },
  methods: {
    ...mapActions({
      getCampaignData: "appData/getCampaignData",
      startCampaignAPI: "appData/startCampaign",
      exportCampaignData: "appData/exportCampaignData",
      updateCampaignNotes: "appData/updateCampaignNotes",
    }),

    async loadCampaignData() {
      try {
        this.isLoading = true;
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          search: this.searchQuery,
          from_date: this.filters.fromDate,
          to_date: this.filters.toDate,
          status: this.filters.status,
        };

        const response = await this.getCampaignData(params);
        if (response.status === 200) {
          this.campaignResults = response.data.results || [];
          this.totalRows = response.data.count || 0;
          this.updateStats(response.data.stats);
        }
      } catch (error) {
        console.error("Error loading campaign data:", error);
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Error",
              variant: "danger",
              icon: "BellIcon",
            },
          },
          {
            position: "top-right",
          }
        );
        
      } finally {
        this.isLoading = false;
      }
    },

    updateStats(statsData) {
      if (statsData) {
        this.stats = {
          totalCalls: statsData.total_calls || 0,
          interested: statsData.interested || 0,
          notInterested: statsData.not_interested || 0,
        };
      }
    },

    async startCampaign() {
      try {
        this.isStartingCampaign = true;
        const response = await this.startCampaignAPI();
        if (response.status === 200 || response.status === 201) {

          
        this.$toast({
          component: "ToastificationContent",
          props: {
            text: "Campaign started successfully!",
            icon: "AlertCircleIcon",
            variant: "success",
          },
        });

          
          // Reload data after starting campaign
          setTimeout(() => {
            this.loadCampaignData();
          }, 2000);
        }
      } catch (error) {
        console.error("Error starting campaign:", error);
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Error",
              variant: "danger",
              icon: "BellIcon",
            },
          },
          {
            position: "top-right",
          }
        );
      } finally {
        this.isStartingCampaign = false;
      }
    },

    onDateFilterChange() {
      this.currentPage = 1;
      this.loadCampaignData();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadCampaignData();
    },

    onSearch() {
      this.currentPage = 1;
      this.loadCampaignData();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadCampaignData();
    },

    resetFilters() {
      this.filters = {
        fromDate: null,
        toDate: null,
        status: null,
      };
      this.searchQuery = "";
      this.currentPage = 1;
      this.loadCampaignData();
    },

    async exportData() {
      try {
        this.isExporting = true;
        const params = {
          from_date: this.filters.fromDate,
          to_date: this.filters.toDate,
          status: this.filters.status,
          search: this.searchQuery,
        };

        const response = await this.exportCampaignData(params);
        if (response.status === 200) {
          // Handle file download
          const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `campaign_results_${moment().format("YYYY-MM-DD")}.xlsx`;
          link.click();

          this.$toast(
            ToastificationContent,
            {
              props: {
                title: "Export Success",
                text: "Data exported successfully!",
                icon: "DownloadIcon",
                variant: "success",
              },
            },
            {
              position: "top-right",
            }
          );
        }
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$toast(
          ToastificationContent,
          {
            props: {
              title: "Export Failed",
              text: "Failed to export campaign data",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          },
          {
            position: "top-right",
          }
        );
      } finally {
        this.isExporting = false;
      }
    },

    viewDetails(item) {
      this.selectedItem = item;
      this.showDetailsModal = true;
    },

    callAgain(item) {
      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to call ${item.customer_name} again?`, {
          title: "Confirm Call",
          size: "sm",
          buttonSize: "sm",
          okVariant: "primary",
          okTitle: "Call",
          cancelTitle: "Cancel",
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.$toast(
              ToastificationContent,
              {
                props: {
                  title: "Calling",
                  text: `Initiating call to ${item.customer_name}...`,
                  icon: "PhoneIcon",
                  variant: "info",
                },
              },
              {
                position: "top-right",
              }
            );
            // Implement call API here
          }
        });
    },

    addNotes(item) {
      this.currentItem = item;
      this.currentNotes = item.notes || "";
      this.$bvModal.show("notes-modal");
    },

    async saveNotes() {
      if (!this.currentNotes.trim()) return;

      try {
        const response = await this.updateCampaignNotes({
          id: this.currentItem.id,
          notes: this.currentNotes,
        });

        if (response.status === 200) {
          this.$toast(
            ToastificationContent,
            {
              props: {
                title: "Notes Saved",
                text: "Call notes have been saved successfully",
                icon: "CheckCircleIcon",
                variant: "success",
              },
            },
            {
              position: "top-right",
            }
          );

          // Update local data
          const index = this.campaignResults.findIndex((item) => item.id === this.currentItem.id);
          if (index !== -1) {
            this.campaignResults[index].notes = this.currentNotes;
          }
        }
      } catch (error) {
        console.error("Error saving notes:", error);
        this.$toast(
          ToastificationContent,
          {
            props: {
              title: "Error",
              text: "Failed to save notes",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          },
          {
            position: "top-right",
          }
        );
      }
    },

    cancelNotes() {
      this.currentNotes = "";
      this.currentItem = null;
    },

    getStatusVariant(status) {
      const variants = {
        completed: "success",
        pending: "warning",
        failed: "danger",
        in_progress: "info",
        answered: "success",
        not_answered: "secondary",
        busy: "warning",
      };
      return variants[status] || "secondary";
    },

    formatStatus(status) {
      return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    formatDuration(seconds) {
      if (!seconds) return "0:00";
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    },

    formatDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY HH:mm");
    },

    formatNotes(notes) {
      if (!notes) return [];
      if (typeof notes === 'string') {
        try {
          const parsedNotes = JSON.parse(notes);
          if (typeof parsedNotes === 'object' && parsedNotes !== null) {
            return Object.entries(parsedNotes)
              .map(([key, value]) => {
                if (value && typeof value === 'object' && Object.keys(value).length === 0) {
                  return null;
                }
                if (value === null || value === undefined || value === '') return null;
                return `${key}: ${value}`;
              })
              .filter(note => note && note.trim());
          }
        } catch (e) {
          // If parsing fails, treat as a regular string
          return notes.split('\n').filter(note => note.trim());
        }
      }
      if (typeof notes === 'object') {
        return Object.entries(notes)
          .map(([key, value]) => {
            if (value && typeof value === 'object' && Object.keys(value).length === 0) {
              return null;
            }
            if (value === null || value === undefined || value === '') return null;
            return `${key}: ${value}`;
          })
          .filter(note => note && note.trim());
      }
      return [];
    },
  },
};
</script>

<style scoped>
.campaign-management {
  padding: 0.5rem;
}

.stat-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

/* Alternative approach - lower z-index for stat cards */
.stat-card {
  z-index: 1;
  position: relative;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-light-primary {
  background-color: rgba(0, 168, 204, 0.1);
}

.bg-light-success {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-light-info {
  background-color: rgba(23, 162, 184, 0.1);
}

.gap-1 {
  gap: 0.25rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.badge {
  font-weight: 500;
  padding: 0.5rem 0.75rem;
}

@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }
}

.stat-card {
  z-index: 1;
  position: relative;
}


</style>

